import { Turnstile, turnstileSiteKey } from '@/components/auth/Turnstile';
import { useState, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import PageLayout from '@/components/layout/PageLayout';
import { toast } from '@/hooks/use-toast';
import { SectionHeading } from '@/components/ui/section-heading';

const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LocationState {
  email?: string;
  fromRegistration?: boolean;
}

export default function Login() {
  const { user, signIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const location = useLocation();
  const locationState = location.state as LocationState | undefined;
  
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: locationState?.email || '',
      password: '',
    },
  });

  // Show notification if redirected from registration
  useEffect(() => {
    if (locationState?.fromRegistration) {
      // Don't show toast here as it's already handled in the Register component
    }
  }, [locationState]);

  const onSubmit = async (data: LoginFormValues) => {
    if (turnstileSiteKey && !captchaToken) return;
    setIsLoading(true);
    try {
      await signIn(data.email, data.password, captchaToken);
    } catch (error: any) {
      console.error('Login error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to login. Please check your credentials.'
      });
    } finally {
      setCaptchaToken('');
      setCaptchaResetKey((key) => key + 1);
      setIsLoading(false);
    }
  };

  // Redirect if user is already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLayout>
      {/* Hero Section */}
      <div className="pt-16 pb-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">Members Area</h1>
            <p className="text-xl text-gray-300">Unlock your training journey – access your personalised curriculum, watch training videos, view upcoming events, and track your advancement.</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto space-y-6">
          <SectionHeading
            title="Login"
            gradient={true}
          />
          <p className="text-center text-gray-500">Enter your details to access your account</p>

          {locationState?.fromRegistration && (
            <Alert>
              <AlertDescription>
                This email is already registered. Please log in with your existing credentials.
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="email@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input placeholder="••••••••" type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Turnstile onToken={setCaptchaToken} resetKey={captchaResetKey} />

              <Button type="submit" className="w-full" disabled={isLoading || Boolean(turnstileSiteKey && !captchaToken)}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </Form>

          <div className="text-center space-y-2">
            <p className="text-sm text-gray-500">
              Don't have an account?{' '}
              <Link to="/auth/register" className="text-taekwondo-red hover:underline">
                Register
              </Link>
            </p>
            <p className="text-sm text-gray-500">
              <Link to="/auth/forgot-password" className="text-taekwondo-blue hover:underline">
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
